import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';
import * as WondeSyncActions from './wonde-sync.actions';
import { WondeSyncService } from '@core/services/wonde-sync.service';

/** Reduces the backend's FAILED status code, an HttpErrorResponse or an Error to one message. */
export function extractWondeSyncErrorMessage(error: any): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error?.status === 'FAILED' && error?.reason) {
    return error.reason;
  }
  if (error?.error?.statusCode?.reason) {
    return error.error.statusCode.reason;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

@Injectable()
export class WondeSyncEffects {
  constructor(private actions$: Actions, private wondeSyncService: WondeSyncService) {}

  loadMismatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WondeSyncActions.loadMismatches),
      switchMap(() =>
        this.wondeSyncService.loadMismatches().pipe(
          map((mismatches) => WondeSyncActions.loadMismatchesSuccess({ mismatches })),
          catchError((error) => of(WondeSyncActions.loadMismatchesFailure({ error: extractWondeSyncErrorMessage(error) })))
        )
      )
    )
  );

  loadSkippedMismatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WondeSyncActions.loadSkippedMismatches),
      switchMap(() =>
        this.wondeSyncService.loadSkippedMismatches().pipe(
          map((mismatches) => WondeSyncActions.loadSkippedMismatchesSuccess({ mismatches })),
          catchError((error) => of(WondeSyncActions.loadSkippedMismatchesFailure({ error: extractWondeSyncErrorMessage(error) })))
        )
      )
    )
  );

  linkMismatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WondeSyncActions.linkMismatch),
      mergeMap(({ request }) =>
        this.wondeSyncService.link(request).pipe(
          map((mismatchId) => WondeSyncActions.linkMismatchSuccess({ mismatchId })),
          catchError((error) => of(WondeSyncActions.linkMismatchFailure({
            mismatchId: request.mismatchId,
            error: extractWondeSyncErrorMessage(error)
          })))
        )
      )
    )
  );

  createPupil$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WondeSyncActions.createPupil),
      mergeMap(({ mismatchId }) =>
        this.wondeSyncService.createPupil({ mismatchId }).pipe(
          map((id) => WondeSyncActions.createPupilSuccess({ mismatchId: id })),
          catchError((error) => of(WondeSyncActions.createPupilFailure({
            mismatchId,
            error: extractWondeSyncErrorMessage(error)
          })))
        )
      )
    )
  );

  dismissMismatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WondeSyncActions.dismissMismatch),
      mergeMap(({ mismatchId, note }) =>
        this.wondeSyncService.dismiss({ mismatchId, note }).pipe(
          map((id) => WondeSyncActions.dismissMismatchSuccess({ mismatchId: id })),
          catchError((error) => of(WondeSyncActions.dismissMismatchFailure({
            mismatchId,
            error: extractWondeSyncErrorMessage(error)
          })))
        )
      )
    )
  );

  assignGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WondeSyncActions.assignGroups),
      mergeMap(({ request }) =>
        this.wondeSyncService.assignGroups(request).pipe(
          map((mismatchId) => WondeSyncActions.assignGroupsSuccess({ mismatchId })),
          catchError((error) => of(WondeSyncActions.assignGroupsFailure({
            mismatchId: request.mismatchId,
            error: extractWondeSyncErrorMessage(error)
          })))
        )
      )
    )
  );
}
